import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService, userService, apiService } from '../services';
import { hasLocalAuthEvidence } from '../utils/authUtils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [lastAuthCheck, setLastAuthCheck] = useState(0);
  const [authCheckInProgress, setAuthCheckInProgress] = useState(false);
  const [lastUnauthedAt, setLastUnauthedAt] = useState(0);
  const [authModalView, setAuthModalView] = useState(null);
  const [attemptedRestrictedAccess, setAttemptedRestrictedAccess] = useState(false);
  const [redirectingFromRestricted, setRedirectingFromRestricted] = useState(false);

  const openAuthModal = (view = 'signin', isRestrictedPageAccess = false) => {
    // Don't open modal if we're in the middle of redirecting from a restricted page
    if (redirectingFromRestricted) {
      return;
    }
    
    setAuthModalView(view);
    if (isRestrictedPageAccess) {
      setAttemptedRestrictedAccess(true);
    }
  };
  
  const closeAuthModal = (onCloseCallback) => {
    // If user was trying to access a restricted page and closes modal without authenticating,
    // redirect them to explore page immediately
    if (attemptedRestrictedAccess && !token) {
      setAttemptedRestrictedAccess(false);
      setAuthModalView(null);
      setRedirectingFromRestricted(true);
      
      if (onCloseCallback) {
        // Use replace: true to prevent back navigation to the restricted page
        onCloseCallback('/explore', { replace: true });
      }
      
      // Clear the redirecting flag after navigation
      setTimeout(() => {
        setRedirectingFromRestricted(false);
      }, 100);
      return;
    }
    
    setAuthModalView(null);
    setAttemptedRestrictedAccess(false);
    if (onCloseCallback) {
      onCloseCallback();
    }
  };

  /**
   * Close the auth modal and navigate in one step (e.g. "Forgot password?").
   * Does not apply the restricted-flow redirect to /explore — that was racing with a second
   * navigate() and could leave the UI out of sync with the URL on first attempt.
   * If the user had opened the modal from a restricted page, uses replace: true (merged with
   * navigationOptions) so history does not return to the gated route.
   */
  const closeAuthModalAndNavigate = (navigate, path, navigationOptions = {}) => {
    if (!navigate || !path) {
      return;
    }
    const wasRestricted = attemptedRestrictedAccess;
    setAuthModalView(null);
    setAttemptedRestrictedAccess(false);
    navigate(path, {
      ...navigationOptions,
      ...(wasRestricted ? { replace: true } : {}),
    });
  };

  // Check authentication status on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Listen for authentication changes from extension
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'UCART_AUTH_CHECK') {
        // Extension is asking for auth status, send current status
        window.postMessage({
          type: 'GOBAG_AUTH_STATUS',
          authenticated: !!token,
          token: token,
          user: user
        }, '*');
      } else if (event.data && event.data.type === 'UCART_LOGIN_REQUEST') {
        // Extension needs user to login - only check if it's been more than 30 seconds
        const now = Date.now();
        if (now - lastAuthCheck > 30000) {
          checkAuthStatus();
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [token, user, lastAuthCheck]);

  // Periodically check if auth evidence disappears (user manually clears cookies/localStorage)
  useEffect(() => {
    // Only run this check if we think the user is authenticated
    if (!token || !user) return;

    const intervalId = setInterval(() => {
      // If we had auth but now have no local evidence, recheck auth status
      if (!hasLocalAuthEvidence()) {
        console.log('Auth evidence disappeared, rechecking auth status');
        checkAuthStatus();
      }
    }, 60000); // Check every minute

    return () => clearInterval(intervalId);
  }, [token, user]);

  // Initialize user from localStorage if available
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    
    if (storedToken && storedUser && !user) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setToken(storedToken);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
  }, [user]);

  const checkAuthStatus = async () => {
    // Prevent multiple simultaneous auth checks
    if (authCheckInProgress) {
      return;
    }

    // If we recently checked (within 2 minutes) and have valid token/user, skip
    const now = Date.now();
    if (now - lastAuthCheck < 120000 && token && user) {
      setLoading(false);
      return;
    }

    // If we recently confirmed unauthenticated (within 15s), short-circuit to avoid spinner
    if (!token && !user && now - lastUnauthedAt < 15000) {
      setLoading(false);
      return;
    }

    // OPTIMIZATION: Check for local auth evidence before making API call
    // If there's no localStorage token and no auth cookies, skip the API call
    // This prevents unnecessary /api/auth/status requests for unauthenticated users
    if (!hasLocalAuthEvidence()) {
      console.log('AuthContext: No local auth evidence (no localStorage token or auth cookies), skipping /api/auth/status request');
      setUser(null);
      setToken(null);
      setLastUnauthedAt(now);
      setLoading(false);
      return;
    }

    setAuthCheckInProgress(true);
    setLastAuthCheck(now);

    try {
      // Check server-side authentication status (sessions + cookies)
      const result = await authService.checkAuthStatus();

      if (result.success && result.data.authenticated) {
        setUser(result.data.user);
        // If we don't have a token in localStorage but server says we're authenticated,
        // we need to get the token (likely from cookie)
        if (!token) {
          // Make a validation request to get the token
          const tokenResult = await authService.validateToken();
          if (tokenResult.success && tokenResult.data.isValid) {
            // We can't get the actual token from httpOnly cookie,
            // but we know the user is authenticated
            setToken('authenticated_via_cookie');
            localStorage.setItem('token', 'authenticated_via_cookie');
            localStorage.setItem('user', JSON.stringify(result.data.user));
          }
        } else {
          // Update stored user data if it changed
          localStorage.setItem('user', JSON.stringify(result.data.user));
        }
      } else {
        // Not authenticated, clear local state
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setLastUnauthedAt(Date.now());
      }
    } catch (error) {
      console.error('Auth status check failed:', error);
      // Fall back to localStorage check
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        } catch (parseError) {
          console.error('Error parsing stored user:', parseError);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
          setLastUnauthedAt(Date.now());
        }
      } else {
        // If no local creds either, consider unauthenticated to avoid long spinners
        setLastUnauthedAt(Date.now());
      }
    } finally {
      setLoading(false);
      setAuthCheckInProgress(false);
    }
  };

  const login = async (email, password) => {
    const result = await authService.login(email, password);

    if (result.success) {
      const { access_token, user } = result.data;
      setToken(access_token);
      setUser(user);
      setLastAuthCheck(Date.now());
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
    }

    return result;
  };

  const register = async (email, password, username) => {
    return await authService.register(email, password, username);
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      setLastAuthCheck(0);
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Notify extension about logout
      window.postMessage(
        { type: 'GOBAG_LOGOUT' },
        '*'
      );
    }
  };

  const getProfile = async () => {
    return await userService.getProfile();
  };

  const updateProfile = async (profileData) => {
    const result = await userService.updateProfile(profileData);
    if (result.success) {
      // Update local user state
      const updatedUser = { ...user, ...result.data };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
    return result;
  };

  const uploadProfilePicture = async (file) => {
    const result = await userService.uploadProfilePicture(file);
    if (result.success && result.data.profile_picture_url) {
      // Update local user state with new profile picture
      const updatedUser = { ...user, profile_picture_url: result.data.profile_picture_url };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
    return result;
  };

  const deleteProfilePicture = async () => {
    const result = await userService.deleteProfilePicture();
    if (result.success) {
      const updatedUser = { ...user, profile_picture_url: null };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
    return result;
  };

  /** After password change: persist new JWT and keep user logged in. */
  const updateAccessToken = (newToken) => {
    setToken(newToken);
    setLastAuthCheck(Date.now());
    localStorage.setItem('token', newToken);
    apiService.clearCache('GET:/api/auth/status');
    if (user) {
      window.postMessage(
        { type: 'GOBAG_LOGIN_SUCCESS', token: newToken, user },
        '*'
      );
    }
  };

  // Set authentication data directly (used for OAuth flows)
  const setAuthData = (userData, accessToken) => {
    setToken(accessToken);
    setUser(userData);
    setLastAuthCheck(Date.now());
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
   
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    getProfile,
    updateProfile,
    uploadProfilePicture,
    deleteProfilePicture,
    updateAccessToken,
    checkAuthStatus,
    setAuthData,
    isAuthenticated: !!token,
    authModalView,
    openAuthModal,
    closeAuthModal,
    closeAuthModalAndNavigate
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 